import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { api } from "../../lib/api";
import { invalidateContents } from "../../hooks/useContents";
import { ImageField } from "./ImageField";

const SECTIONS = [
  {
    title: "Testi della homepage",
    fields: [
      { key: "hero_line1", label: "Hero — prima riga del titolo" },
      { key: "hero_line2", label: "Hero — seconda riga (evidenziata in verde)" },
      { key: "hero_subtitle", label: "Hero — sottotitolo" },
      { key: "hero_text", label: "Hero — testo descrittivo", multi: true },
      { key: "about_p1", label: "Chi siamo — paragrafo 1", multi: true },
      { key: "about_p2", label: "Chi siamo — paragrafo 2", multi: true },
      { key: "about_p3", label: "Chi siamo — paragrafo 3", multi: true },
      { key: "cta_title", label: "CTA finale — titolo (vai a capo per due righe)", multi: true },
      { key: "cta_text", label: "CTA finale — testo", multi: true },
    ],
  },
  {
    title: "Immagini principali",
    fields: [
      { key: "hero_img", label: "Hero — grande foto di apertura", image: true },
      { key: "about_img", label: "Chi siamo — foto", image: true },
      { key: "cta_img", label: "CTA finale — sfondo", image: true },
      { key: "privati_img", label: "Blocco Privati — foto", image: true },
      { key: "aziende_img", label: "Blocco Aziende — foto", image: true },
    ],
  },
  {
    title: "Contatti",
    fields: [
      { key: "contact_email", label: "Email pubblica del sito" },
      { key: "contact_whatsapp", label: "Numero WhatsApp (solo cifre, con prefisso: 39...)" },
    ],
  },
  {
    title: "Zone operative",
    fields: [
      { key: "zones", label: "Zone servite (separate da virgola — appaiono nel nastro scorrevole e nella pagina Contatti)", multi: true },
    ],
  },
];

export const ContentsTab = () => {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api("/contents").then(setValues).catch((e) => toast.error(e.message));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api("/admin/contents", { method: "PUT", body: values });
      invalidateContents();
      toast.success("Contenuti aggiornati. Ricarica le pagine del sito per vederli.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!values) return <Loader2 className="animate-spin text-forest-glow mx-auto" size={28} />;

  return (
    <div data-testid="admin-contents" className="max-w-2xl space-y-10">
      <p className="text-sm text-cement border border-steel bg-card px-5 py-4">
        Personalizza il sito senza toccare il codice: testi, immagini, contatti e zone operative. I lavori in galleria si gestiscono nella scheda "Lavori".
      </p>
      {SECTIONS.map((s) => (
        <section key={s.title}>
          <h3 className="font-display font-bold text-base text-white uppercase tracking-tight border-b border-steel pb-3 mb-5">{s.title}</h3>
          <div className="space-y-5">
            {s.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-metal/80 mb-2">{f.label}</label>
                {f.image ? (
                  <ImageField value={values[f.key]} onChange={(url) => setValues({ ...values, [f.key]: url })} testid={`admin-content-${f.key}`} />
                ) : f.multi ? (
                  <textarea data-testid={`admin-content-${f.key}`} rows={3} className="field-input resize-y" value={values[f.key] || ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
                ) : (
                  <input data-testid={`admin-content-${f.key}`} className="field-input" value={values[f.key] || ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
      <button data-testid="admin-contents-save" onClick={save} disabled={saving} className="btn-primary disabled:opacity-60">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Salva tutte le modifiche
      </button>
    </div>
  );
};
